/**
 * Wave 3.59 — Broken Recut Issues list page.
 * Shows every outstanding (and, on request, past) broken-packet recut
 * issue for the current company. From here the operator triggers the
 * Receive Recut flow.
 */
import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { PackageCheck, RefreshCw, Scissors, XCircle } from 'lucide-react';
import { formatCarats, formatINR, formatDate } from '@/lib/format';
import { extractError } from '@/lib/errors';
import ReceiveRecutDialog from '@/components/broken-packets/ReceiveRecutDialog';

const STATUS_STYLE = {
  issued:    'bg-amber-100 text-amber-800 border-amber-200',
  received:  'bg-emerald-100 text-emerald-800 border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-700 border-slate-200',
};

const MATERIAL_LABEL = {
  diamond: 'Diamonds',
  color_stone: 'Color Stones',
  pearl: 'Pearls',
};

export default function BrokenPacketIssues() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell
      title="Broken Recut Issues"
      subtitle="Track broken raw material issued to vendors or cutters for re-cutting."
    >
      <CompanyGate>
        <BrokenIssuesContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function BrokenIssuesContent({ companyId }) {
  const { hasMaterialAccess } = useAuth();
  // Wave 3.99q — Only surface material options the operator can
  // actually ``see``. Diamond Buyer sees Diamonds, CS+Pearl Buyer
  // sees Colour Stones + Pearls, Karigar / SA / CA all see the
  // full set. The backend enforces the same rule as a safety net.
  const availableMaterials = useMemo(() => {
    const mats = [];
    if (hasMaterialAccess('diamond', 'see')) mats.push({ value: 'diamond', label: 'Diamonds' });
    if (hasMaterialAccess('color_stone', 'see')) mats.push({ value: 'color_stone', label: 'Color Stones' });
    if (hasMaterialAccess('pearl', 'see')) mats.push({ value: 'pearl', label: 'Pearls' });
    return mats;
  }, [hasMaterialAccess]);
  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('issued');
  const [materialFilter, setMaterialFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState(null);

  // Wave 3.99q — If the operator has exactly one material in scope,
  // default the filter to that material so the "All materials"
  // pseudo-value never leaks (it also doesn't appear in the dropdown
  // in that case).
  useEffect(() => {
    if (availableMaterials.length === 1 && materialFilter === 'all') {
      setMaterialFilter(availableMaterials[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMaterials]);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = { company_id: companyId, status: statusFilter };
      if (materialFilter !== 'all') params.material_kind = materialFilter;
      const { data } = await api.get('/broken-packet-issues', { params });
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load issues'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyId, statusFilter, materialFilter]);

  const cancelIssue = async (id) => {
    if (!window.confirm('Cancel this recut issue? The broken packet balance will be restored.')) return;
    try {
      await api.post(`/broken-packet-issues/${id}/cancel`, null, { params: { note: 'Cancelled from list' } });
      toast.success('Issue cancelled');
      load();
    } catch (e) {
      toast.error(extractError(e, 'Cancel failed'));
    }
  };

  return (
    <div className="space-y-4">
      <div className="sticky-filter-bar flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg px-3" data-testid="broken-issues-filter-bar">
        <div className="w-40">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="broken-issue-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="issued">Outstanding</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Select value={materialFilter} onValueChange={setMaterialFilter}>
            <SelectTrigger data-testid="broken-issue-material-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {/* Wave 3.99q — Only show "All materials" when the user
                  has more than one material in scope. If they only
                  have one, that single option is the effective
                  filter. */}
              {availableMaterials.length > 1 && (
                <SelectItem value="all">All materials</SelectItem>
              )}
              {availableMaterials.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1" />
        <Button variant="outline" onClick={load} data-testid="broken-issue-refresh">
          <RefreshCw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2.5 text-[11px] uppercase tracking-wider text-slate-500 border-b border-[var(--divider)]">
          <div className="col-span-2">Memo</div>
          <div className="col-span-1">Material</div>
          <div className="col-span-2">Broken Packet</div>
          <div className="col-span-2">Recipient</div>
          <div className="col-span-1">Issued</div>
          <div className="col-span-1 text-right">Carats</div>
          <div className="col-span-1 text-right">Pieces</div>
          <div className="col-span-1 text-right">Value</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {loading && (
          <div className="px-4 py-6 text-sm text-slate-500">Loading…</div>
        )}
        {!loading && list.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            <Scissors className="mx-auto mb-2" size={22} />
            No recut issues match your filters.
          </div>
        )}
        {list.map((iss) => (
          <div
            key={iss.id}
            className="grid grid-cols-12 px-4 py-3 items-center border-b border-[var(--divider)] hover:bg-slate-50"
            data-testid={`broken-issue-row-${iss.id}`}
          >
            <div className="col-span-2 font-mono text-xs text-[var(--ink-navy)]">{iss.issue_memo_no}</div>
            <div className="col-span-1 text-xs text-slate-600">{MATERIAL_LABEL[iss.material_kind] || iss.material_kind}</div>
            <div className="col-span-2 text-sm font-medium">{iss.broken_packet_no}</div>
            <div className="col-span-2 text-sm">
              <div className="font-medium">{iss.recipient_name}</div>
              <div className="text-[11px] text-slate-500 capitalize">{iss.recipient_type}</div>
            </div>
            <div className="col-span-1 text-[11px] text-slate-500">{formatDate(iss.issued_at)}</div>
            <div className="col-span-1 text-right font-mono tabular-nums text-sm">{formatCarats(iss.issued_carats)}</div>
            <div className="col-span-1 text-right font-mono tabular-nums text-sm">{iss.issued_pieces}</div>
            <div className="col-span-1 text-right font-mono tabular-nums text-sm">{formatINR(iss.issued_value)}</div>
            <div className="col-span-1 flex justify-end gap-1">
              <Badge variant="outline" className={`text-[10px] ${STATUS_STYLE[iss.status] || ''}`}>{iss.status}</Badge>
            </div>
            {iss.status === 'issued' && (
              <div className="col-span-12 mt-2 flex justify-end gap-2">
                <Button
                  size="sm" variant="outline"
                  onClick={() => cancelIssue(iss.id)}
                  data-testid={`broken-issue-cancel-${iss.id}`}
                >
                  <XCircle size={14} className="mr-1" /> Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => setReceiveTarget(iss)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`broken-issue-receive-${iss.id}`}
                >
                  <PackageCheck size={14} className="mr-1" /> Receive Recut
                </Button>
              </div>
            )}
            {iss.status === 'received' && iss.target_packet_no && (
              <div className="col-span-12 mt-1 text-[11px] text-slate-500">
                Received {formatCarats(iss.returned_carats)}ct / {iss.returned_pieces}pcs into <b>{iss.target_packet_no}</b>
                {iss.cut_loss_carats > 0 && (
                  <span> · cut loss <b>{formatCarats(iss.cut_loss_carats)}ct</b></span>
                )}
              </div>
            )}
          </div>
        ))}
      </Card>

      <ReceiveRecutDialog
        open={Boolean(receiveTarget)}
        onOpenChange={(v) => !v && setReceiveTarget(null)}
        issue={receiveTarget}
        onReceived={() => { setReceiveTarget(null); load(); }}
      />
    </div>
  );
}
